import type {
  AssistantResponse,
  CaseDetail,
  CaseListResponse,
  CurriculumCatalog,
  CurriculumEntity,
  DashboardPeriod,
  EvaluationReport,
  KnowledgeGraph,
  ProjectHistoryEvent,
  ProjectArtifact,
  ProjectArtifactTarget,
  ProjectArtifactType,
  ProjectRecord,
  ProjectStage,
  SessionUser,
  ProjectNote,
  ProjectNoteSplitResponse,
  TeacherDashboard,
  PptEvaluation,
  ProjectGeneratedImage,
  ProjectImageQuota,
  SpatialCatalog,
  SpatialPreview,
} from "./types";

const headers = { "Content-Type": "application/json" };

export type AssistantPayload = {
  message: string;
  context_type: "general" | "knowledge" | "case" | "project" | "evaluation";
  context_id?: string;
  project_snapshot?: Record<string, unknown>;
  action?: "knowledge" | "cases" | "next" | "logic" | "card_review" | "consultation" | "analysis_prompt" | "analysis_code" | "image_prompt" | "ppt_outline" | "free";
};

export type AssistantResource = {
  kind: "knowledge" | "case";
  id: string;
  title: string;
  description: string;
  meta: string;
  unit_id?: string | null;
  image_url: string;
};

export type AssistantStreamEvent =
  | { type: "status"; message: string }
  | { type: "meta"; sources: string[]; mode: "model" | "guidance"; task_type: string; retrieval_ms?: number }
  | { type: "delta"; delta: string }
  | { type: "done"; sources: string[]; mode: "model" | "guidance"; task_type: string; first_token_ms?: number; total_ms?: number };

export type ProjectArtifactStreamEvent = AssistantStreamEvent
  | { type: "artifact"; artifact: ProjectArtifact }
  | { type: "code"; code: string }
  | { type: "error"; message: string };

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...headers, ...options?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new ApiError(body?.detail || `请求失败 (${response.status})`, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function streamAssistant(
  payload: AssistantPayload,
  onEvent: (event: AssistantStreamEvent) => void,
  signal?: AbortSignal,
) {
  const response = await fetch("/api/assistant/chat/stream", {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new ApiError(body?.detail || `请求失败 (${response.status})`, response.status);
  }
  if (!response.body) throw new ApiError("浏览器无法读取流式回答", 500);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;

  const parseLine = (line: string) => {
    if (!line.trim()) return;
    const event = JSON.parse(line) as AssistantStreamEvent;
    if (event.type === "done") completed = true;
    onEvent(event);
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    lines.forEach(parseLine);
    if (done) break;
  }
  parseLine(buffer);
  if (!completed) throw new ApiError("回答传输提前中断，请重新发送一次", 502);
}

async function streamProjectGeneration(
  url: string,
  payload: Record<string, unknown>,
  onEvent: (event: ProjectArtifactStreamEvent) => void,
  completionType: "artifact" | "code",
) {
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new ApiError(body?.detail || `请求失败 (${response.status})`, response.status);
  }
  if (!response.body) throw new ApiError("浏览器无法读取流式内容", 500);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;
  const parseLine = (line: string) => {
    if (!line.trim()) return;
    const event = JSON.parse(line) as ProjectArtifactStreamEvent;
    if (event.type === "error") throw new ApiError(event.message, 502);
    if (event.type === completionType) completed = true;
    onEvent(event);
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) parseLine(line);
    if (done) break;
  }
  parseLine(buffer);
  if (!completed) throw new ApiError("生成传输提前中断，请重新生成一次", 502);
}

export const api = {
  login: (payload: { role: "student" | "teacher"; identifier: string; password: string; remember: boolean }) =>
    request<SessionUser>("/api/auth/login", { method: "POST", body: JSON.stringify(payload) }),
  session: () => request<SessionUser>("/api/auth/session"),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  knowledge: () => request<KnowledgeGraph>("/api/knowledge/graph"),
  curriculum: () => request<CurriculumCatalog>("/api/knowledge/curriculum"),
  knowledgeEntity: (entityId: string) =>
    request<CurriculumEntity>(`/api/knowledge/entities/${encodeURIComponent(entityId)}`),
  cases: (query = "", theme = "全部", limit = 60) =>
    request<CaseListResponse>(
      `/api/cases?query=${encodeURIComponent(query)}&theme=${encodeURIComponent(theme)}&limit=${limit}`,
    ),
  caseDetail: (caseId: string) => request<CaseDetail>(`/api/cases/${caseId}`),
  assistant: (payload: AssistantPayload) =>
    request<AssistantResponse>("/api/assistant/chat", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  assistantStream: streamAssistant,
  assistantResources: (payload: { kind: "knowledge" | "case"; project_snapshot: Record<string, unknown>; limit?: number }) =>
    request<{ items: AssistantResource[] }>("/api/assistant/resources", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  listProjects: () => request<ProjectRecord[]>("/api/projects"),
  projectArtifacts: (projectId: string) => request<ProjectArtifact[]>(`/api/projects/${projectId}/artifacts`),
  generateProjectArtifact: (
    projectId: string,
    payload: { artifact_type: ProjectArtifactType; target_tool: ProjectArtifactTarget; source_stage?: string; instructions?: string },
  ) => request<ProjectArtifact>(`/api/projects/${projectId}/artifacts/generate`, { method: "POST", body: JSON.stringify(payload) }),
  generateProjectArtifactStream: (
    projectId: string,
    payload: { artifact_type: ProjectArtifactType; target_tool: ProjectArtifactTarget; source_stage?: string; instructions?: string },
    onEvent: (event: ProjectArtifactStreamEvent) => void,
  ) => streamProjectGeneration(`/api/projects/${projectId}/artifacts/generate/stream`, payload, onEvent, "artifact"),
  updateProjectArtifact: (
    projectId: string,
    artifactId: string,
    payload: { title: string; content: string; status: "draft" | "ready" },
  ) => request<ProjectArtifact>(`/api/projects/${projectId}/artifacts/${artifactId}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteProjectArtifact: (projectId: string, artifactId: string) => request<void>(`/api/projects/${projectId}/artifacts/${artifactId}`, { method: "DELETE" }),
  generateProjectCode: (projectId: string, payload: { artifact_id: string; target_tool: "python" | "qgis"; prompt: string }) =>
    request<{ artifact_id: string; target_tool: "python" | "qgis"; code: string }>(`/api/projects/${projectId}/artifacts/code`, { method: "POST", body: JSON.stringify(payload) }),
  generateProjectCodeStream: (
    projectId: string,
    payload: { artifact_id: string; target_tool: "python" | "qgis"; prompt: string },
    onEvent: (event: ProjectArtifactStreamEvent) => void,
  ) => streamProjectGeneration(`/api/projects/${projectId}/artifacts/code/stream`, payload, onEvent, "code"),
  projectImageQuota: (projectId: string) => request<ProjectImageQuota>(`/api/projects/${projectId}/images/quota`),
  projectGeneratedImages: (projectId: string, artifactId?: string) => request<ProjectGeneratedImage[]>(`/api/projects/${projectId}/generated-images${artifactId ? `?artifact_id=${encodeURIComponent(artifactId)}` : ""}`),
  generateProjectImage: async (projectId: string, payload: { artifactId: string; prompt: string; aspectRatio: string; referenceImage?: File | null }) => {
    const form = new FormData();
    form.append("artifact_id", payload.artifactId);
    form.append("prompt", payload.prompt);
    form.append("aspect_ratio", payload.aspectRatio);
    if (payload.referenceImage) form.append("reference_image", payload.referenceImage);
    const response = await fetch(`/api/projects/${projectId}/images/generate`, { method: "POST", credentials: "include", body: form });
    if (!response.ok) { const body = await response.json().catch(() => null) as { detail?: string } | null; throw new ApiError(body?.detail || `请求失败 (${response.status})`, response.status); }
    return response.json() as Promise<ProjectGeneratedImage>;
  },
  projectHistory: (projectId: string) => request<ProjectHistoryEvent[]>(`/api/projects/${projectId}/history`),
  recordProjectEvent: (projectId: string, payload: { event_type: "ai_guidance" | "consultation"; stage?: ProjectStage; title: string; content: string }) =>
    request<ProjectHistoryEvent>(`/api/projects/${projectId}/history`, { method: "POST", body: JSON.stringify(payload) }),
  createProject: (payload: { title: string; seed_mode: string; initial_note: string }) =>
    request<ProjectRecord>("/api/projects", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateProjectStage: (
    projectId: string,
    payload: { stage: ProjectStage; data: Record<string, unknown>; expected_version: number },
  ) =>
    request<ProjectRecord>(`/api/projects/${projectId}/stage`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  evaluateProject: (projectId: string) =>
    request<EvaluationReport>(`/api/projects/${projectId}/evaluate`, { method: "POST" }),
  deleteProject: (projectId: string) => request<void>(`/api/projects/${projectId}`, { method: "DELETE" }),
  projectNote: (projectId: string) => request<ProjectNote>(`/api/projects/${projectId}/note`),
  saveProjectNote: (projectId: string, payload: { content: string; summary: string }) => request<ProjectNote>(`/api/projects/${projectId}/note`, { method: "PUT", body: JSON.stringify(payload) }),
  splitProjectNote: (projectId: string, payload: { content: string }) => request<ProjectNoteSplitResponse>(`/api/projects/${projectId}/note/split`, { method: "POST", body: JSON.stringify(payload) }),
  recordEvent: (payload: { event_type: "page_view" | "resource_feedback"; resource_type: string; resource_id: string; resource_title: string; value: string }) => request<{ id: string }>("/api/analytics/events", { method: "POST", body: JSON.stringify(payload) }),
  teacherDashboard: (period: DashboardPeriod = "30d") => request<TeacherDashboard>(`/api/analytics/teacher-dashboard?period=${encodeURIComponent(period)}`),
  chengduSpatialCatalog: () => request<SpatialCatalog>("/api/spatial/chengdu/catalog"),
  chengduSpatialPreview: (datasetId: string, limit = 220) => request<SpatialPreview>(`/api/spatial/chengdu/preview?dataset_id=${encodeURIComponent(datasetId)}&limit=${limit}`),
  evaluatePpt: async (projectId: string, file: File, title: string) => {
    const form = new FormData(); form.append("file", file); form.append("title", title);
    const response = await fetch(`/api/projects/${projectId}/ppt-evaluate`, { method: "POST", credentials: "include", body: form });
    if (!response.ok) { const body = await response.json().catch(() => null) as { detail?: string } | null; throw new ApiError(body?.detail || `请求失败 (${response.status})`, response.status); }
    return response.json() as Promise<PptEvaluation>;
  },
};
